import { artworkListItemSchema, artworkVisibilitySchema } from "./artworkItem.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/userArtwork.ts
const getUserArtworksInputSchema = z.object({
	userId: z.coerce.bigint().positive(),
	first: z.coerce.number().int().positive().max(50).optional(),
	after: z.string().min(1).optional(),
	last: z.coerce.number().int().positive().max(50).optional(),
	before: z.string().min(1).optional(),
	visibility: artworkVisibilitySchema.optional(),
	type: z.enum(["artwork", "animation"]).default("artwork").meta({ description: "Profile-page tab selector. `artwork` (default) lists still images, albums **and** animated artworks together — despite the name, it is NOT a still-images-only filter; it matches the pre-orpc-migration GraphQL behaviour of the default \"works\" tab. `animation` narrows to animated artworks only." })
}).refine((v) => !(v.first != null && v.last != null), {
	message: "`first` and `last` are mutually exclusive",
	path: ["last"]
}).refine((v) => v.after == null || v.first != null, {
	message: "`after` requires `first`",
	path: ["after"]
}).refine((v) => v.before == null || v.last != null, {
	message: "`before` requires `last`",
	path: ["before"]
}).refine((v) => v.last == null || v.before != null, {
	message: "`last` requires `before`",
	path: ["last"]
});
const getUserArtworksOutputSchema = z.object({
	data: z.array(artworkListItemSchema),
	pageInfo: z.object({
		startCursor: z.string().min(1).optional(),
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean(),
		hasPreviousPage: z.boolean()
	}),
	totalCount: z.number().int().nonnegative().optional()
});
const userArtworkContract = oc.prefix("/user").tag("User").router({
	getUserArtworksLatest: oc.route({
		summary: "List a user’s artworks, newest first",
		description: "Returns the given user’s artworks ordered by `createdAt` descending. Standard relay cursor pagination: **next page** = `first` + `after` (walks forward in the orderBy sequence → older items), **previous page** = `last` + `before` (walks backward → newer items). Visibility (private/unlisted) is filtered out for non-owner viewers; sensitivity gating mirrors the GraphQL `artworks` resolver.",
		method: "GET",
		path: "/{userId}/artworks/latest"
	}).input(getUserArtworksInputSchema).output(getUserArtworksOutputSchema).errors({}),
	getUserArtworksOldest: oc.route({
		summary: "List a user’s artworks, oldest first",
		description: "Returns the given user’s artworks ordered by `createdAt` ascending. Standard relay cursor pagination: **next page** = `first` + `after` (walks forward → newer items), **previous page** = `last` + `before` (walks backward → older items). Visibility (private/unlisted) is filtered out for non-owner viewers; sensitivity gating mirrors the GraphQL `artworks` resolver.",
		method: "GET",
		path: "/{userId}/artworks/oldest"
	}).input(getUserArtworksInputSchema).output(getUserArtworksOutputSchema).errors({}),
	getUserArtworksMostLiked: oc.route({
		summary: "List a user’s artworks, most-liked first",
		description: "Returns the given user’s artworks ordered by `likedCount` descending. Standard relay cursor pagination: **next page** = `first` + `after` (walks forward → less-liked items), **previous page** = `last` + `before` (walks backward → more-liked items). Visibility (private/unlisted) is filtered out for non-owner viewers; sensitivity gating mirrors the GraphQL `artworks` resolver.",
		method: "GET",
		path: "/{userId}/artworks/most-liked"
	}).input(getUserArtworksInputSchema).output(getUserArtworksOutputSchema).errors({})
});
//#endregion
export { getUserArtworksInputSchema, getUserArtworksOutputSchema, userArtworkContract };
